// SEM-7203 — Departments feature API helpers.
//
// Typed fetch wrappers for /v1/departments endpoints in platform-graph.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

export interface DepartmentRow {
  readonly department_id: string;
  readonly tenant_id: string;
  readonly name: string;
  readonly parent_department_id: string | null;
  readonly parent_department_name: string | null;
  readonly head_employee_id: string | null;
  readonly head_employee_name: string | null;
  readonly cost_center_code: string | null;
  readonly description: string | null;
  readonly lifecycle_state: "active" | "archived";
  readonly created_at: string;
  readonly updated_at: string;
}

export interface ListDepartmentsParams {
  readonly parent_department_id?: string | "null";
  readonly lifecycle_state?: "active" | "archived" | "all";
  readonly limit?: number;
  readonly offset?: number;
}

export interface CreateDepartmentInput {
  readonly name: string;
  readonly parent_department_id?: string | null;
  readonly head_employee_id?: string | null;
  readonly cost_center_code?: string | null;
  readonly description?: string | null;
  readonly lifecycle_state?: "active" | "archived";
}

export interface PatchDepartmentInput {
  readonly name?: string;
  readonly parent_department_id?: string | null;
  readonly head_employee_id?: string | null;
  readonly cost_center_code?: string | null;
  readonly description?: string | null;
  readonly lifecycle_state?: "active" | "archived";
}

export const DEPARTMENTS_QUERY_KEY = ["departments"] as const;

type DepartmentsGet = (
  path: "/v1/departments",
  init?: {
    params?: {
      query?: {
        parent_department_id?: string;
        lifecycle_state?: string;
        limit?: number;
        offset?: number;
      };
    };
  },
) => Promise<EnvelopedFetchResult<{ items: DepartmentRow[] }>>;

export async function fetchDepartments(
  params: ListDepartmentsParams = { lifecycle_state: "all" },
): Promise<DepartmentRow[]> {
  const get = platformGraph.GET as unknown as DepartmentsGet;
  const query: {
    parent_department_id?: string;
    lifecycle_state?: string;
    limit?: number;
    offset?: number;
  } = {};
  if (params.parent_department_id !== undefined) query.parent_department_id = params.parent_department_id;
  if (params.lifecycle_state !== undefined) query.lifecycle_state = params.lifecycle_state;
  if (params.limit !== undefined) query.limit = params.limit;
  if (params.offset !== undefined) query.offset = params.offset;

  const result = await unwrapEnvelope(
    get("/v1/departments", {
      params: { query },
    }),
  );
  return result.items;
}

type DepartmentGet = (
  path: "/v1/departments/{id}",
  init: {
    params: { path: { id: string } };
  },
) => Promise<EnvelopedFetchResult<DepartmentRow>>;

export async function fetchDepartment(departmentId: string): Promise<DepartmentRow> {
  const get = platformGraph.GET as unknown as DepartmentGet;
  return unwrapEnvelope(
    get("/v1/departments/{id}", {
      params: { path: { id: departmentId } },
    }),
  );
}

type DepartmentPost = (
  path: "/v1/departments",
  init: {
    body: CreateDepartmentInput;
  },
) => Promise<EnvelopedFetchResult<DepartmentRow>>;

export async function createDepartment(input: CreateDepartmentInput): Promise<DepartmentRow> {
  const post = platformGraph.POST as unknown as DepartmentPost;
  return unwrapEnvelope(
    post("/v1/departments", {
      body: input,
    }),
  );
}

type DepartmentPatch = (
  path: "/v1/departments/{id}",
  init: {
    params: { path: { id: string } };
    body: PatchDepartmentInput;
  },
) => Promise<EnvelopedFetchResult<DepartmentRow>>;

export async function patchDepartment(
  departmentId: string,
  input: PatchDepartmentInput,
): Promise<DepartmentRow> {
  const patch = platformGraph.PATCH as unknown as DepartmentPatch;
  return unwrapEnvelope(
    patch("/v1/departments/{id}", {
      params: { path: { id: departmentId } },
      body: input,
    }),
  );
}

type DepartmentArchivePost = (
  path: "/v1/departments/{id}/archive",
  init: {
    params: { path: { id: string } };
  },
) => Promise<EnvelopedFetchResult<DepartmentRow>>;

export async function archiveDepartment(departmentId: string): Promise<DepartmentRow> {
  const post = platformGraph.POST as unknown as DepartmentArchivePost;
  return unwrapEnvelope(
    post("/v1/departments/{id}/archive", {
      params: { path: { id: departmentId } },
    }),
  );
}
